import { useMemo, useState } from 'react'
import { Globe, Heart, Layers, LineChart, Sparkles } from 'lucide-react'

interface ProjectItem {
  name: string
  url: string
  description: string
  phase: string
  progress: number
  lifecycle: string
  logo?: string
  investmentRevenue: number
  serviceRevenue: number
  users: number
}

interface ProjectCategory {
  category: string
  items: ProjectItem[]
}

const projects: ProjectCategory[] = [
  {
    category: '문화산업',
    items: [
      {
        name: '세계연예인사주',
        url: 'https://goldsaju.com',
        description: '글로벌 연예인 사주 분석 플랫폼',
        phase: '운영 중',
        progress: 100,
        lifecycle: '정체성 탐색',
        logo: '/logos/goldsaju.png',
        investmentRevenue: 1250000,
        serviceRevenue: 380000,
        users: 12500,
      },
      {
        name: '모나시오',
        url: 'https://monaxio.com',
        description: '동서양 종합 감정기반 팀관리 솔루션',
        phase: '개발 중',
        progress: 65,
        lifecycle: '팀/조직',
        logo: '/logos/monaxio.png',
        investmentRevenue: 450000,
        serviceRevenue: 120000,
        users: 3200,
      },
      {
        name: '메타시비아',
        url: 'https://metacivia.xyz',
        description: '팬덤문화 도시 생성 플랫폼',
        phase: '아이디어 구상',
        progress: 25,
        lifecycle: '팬덤',
        logo: '/logos/metacivia.png',
        investmentRevenue: 150000,
        serviceRevenue: 25000,
        users: 800,
      },
    ],
  },
  {
    category: '공예산업',
    items: [
      {
        name: '하하오스토리',
        url: 'https://hahaostory.com',
        description: '공방세계관 만들기 플랫폼',
        phase: '베타 테스트',
        progress: 80,
        lifecycle: '생산',
        logo: '/logos/hahao.svg',
        investmentRevenue: 680000,
        serviceRevenue: 450000,
        users: 8900,
      },
      {
        name: '하하오플레이스',
        url: 'https://hahaoplace.com',
        description: '세계 공방 정보 아카이브',
        phase: '개발 중',
        progress: 55,
        lifecycle: '생산',
        logo: '/logos/hahao.svg',
        investmentRevenue: 320000,
        serviceRevenue: 215000,
        users: 5600,
      },
      {
        name: '하하오툴',
        url: 'https://hahaotool.com',
        description: '공방 필수 툴 (아이디어 구상 중)',
        phase: '아이디어 구상',
        progress: 15,
        lifecycle: '생산',
        logo: '/logos/hahao.svg',
        investmentRevenue: 80000,
        serviceRevenue: 35000,
        users: 1200,
      },
    ],
  },
  {
    category: '창업자 지원',
    items: [
      {
        name: '저스트언빌트',
        url: 'https://justunbuilt.xyz',
        description: 'VC 창업자 교육 플랫폼',
        phase: '개발 중',
        progress: 70,
        lifecycle: '투자',
        logo: '/logos/unbuilt.png',
        investmentRevenue: 920000,
        serviceRevenue: 280000,
        users: 7800,
      },
      {
        name: '디엠즈',
        url: 'https://deomz.xyz',
        description: '한국 시장상인 운영지원 서비스',
        phase: '베타 테스트',
        progress: 85,
        lifecycle: '생산',
        logo: '/logos/hahao.svg',
        investmentRevenue: 550000,
        serviceRevenue: 410000,
        users: 6200,
      },
      {
        name: '카칭',
        url: 'https://kaching-a0z.pages.dev',
        description: '글로벌 소상공인 운영지원',
        phase: '런칭',
        progress: 95,
        lifecycle: '생산',
        logo: '/logos/hahao.svg',
        investmentRevenue: 1850000,
        serviceRevenue: 720000,
        users: 15600,
      },
      {
        name: '에이전투미',
        url: 'https://www.agentumi.xyz',
        description: 'AI AgentMarket 및 AI agent 검증 및 보험 솔루션',
        phase: '개발 중',
        progress: 60,
        lifecycle: '생산',
        logo: '/logos/agentumi.png',
        investmentRevenue: 350000,
        serviceRevenue: 125000,
        users: 4500,
      },
      {
        name: '웨이블레티카',
        url: 'https://waveletica.xyz',
        description: 'SUI 블록체인 크라우드펀딩',
        phase: '베타 테스트',
        progress: 90,
        lifecycle: '투자',
        logo: '/logos/waveletica.png',
        investmentRevenue: 2100000,
        serviceRevenue: 650000,
        users: 18900,
      },
      {
        name: '글로벌부산',
        url: 'https://globalbusan.xyz',
        description: '지역형 크라우드펀딩 플랫폼',
        phase: '개발 중',
        progress: 60,
        lifecycle: '투자',
        logo: '/logos/globalbusan.png',
        investmentRevenue: 780000,
        serviceRevenue: 340000,
        users: 9200,
      },
    ],
  },
  {
    category: '암호화폐 시스템',
    items: [
      {
        name: '코인펀더멘탈',
        url: 'https://coinfundamental.xyz',
        description: '새로운 코인 투자 전략 분석',
        phase: '개발 중',
        progress: 50,
        lifecycle: '투자',
        logo: '/logos/hahao.svg',
        investmentRevenue: 420000,
        serviceRevenue: 185000,
        users: 5800,
      },
      {
        name: 'SUI 100',
        url: 'https://sui100.pages.dev',
        description: 'SUI 생태계 생성기',
        phase: '베타 테스트',
        progress: 75,
        lifecycle: '투자',
        logo: '/logos/hahao.svg',
        investmentRevenue: 1620000,
        serviceRevenue: 540000,
        users: 14200,
      },
    ],
  },
  {
    category: '기타',
    items: [
      {
        name: '미쉐라이프',
        url: 'https://mixue.life',
        description: '베트남 전기차 충전사업 투자',
        phase: '운영 중',
        progress: 100,
        lifecycle: '투자',
        logo: '/logos/hahao.svg',
        investmentRevenue: 2850000,
        serviceRevenue: 950000,
        users: 22300,
      },
      {
        name: '유튜브지니어스',
        url: 'https://youtubegenius.xyz',
        description: '유튜브 동영상 자동생성 도구',
        phase: '개발 중',
        progress: 40,
        lifecycle: '생산',
        logo: '/logos/hahao.svg',
        investmentRevenue: 280000,
        serviceRevenue: 145000,
        users: 4100,
      },
    ],
  },
]

const lifecycleFlow = [
  {
    stage: '탄생',
    icon: '🌱',
    description: '개인의 시작',
  },
  {
    stage: '정체성 탐색',
    icon: '🧠',
    question: '나는 누구인가?',
    description: '자아 발견과 정체성 확립',
    projects: ['세계연예인사주'],
    color: '#667eea',
  },
  {
    stage: '팀/조직',
    icon: '👥',
    question: '우리는 맞는 팀인가?',
    description: '협업과 조직 구성',
    projects: ['모나시오'],
    color: '#764ba2',
  },
  {
    stage: '생산',
    icon: '🛠️',
    question: '뭘 만들까? 어디서 팔까? 어떻게 키울까?',
    description: '창작, 판매, 성장',
    projects: ['하하오스토리', '하하오플레이스', '하하오툴', '디엠즈', '카칭', '에이전투미', '유튜브지니어스'],
    color: '#f093fb',
  },
  {
    stage: '투자',
    icon: '💰',
    question: '자본은?',
    description: '자금 조달과 투자',
    projects: ['저스트언빌트', '웨이블레티카', '글로벌부산', '코인펀더멘탈', 'SUI 100', '미쉐라이프'],
    color: '#4facfe',
  },
  {
    stage: '팬덤',
    icon: '❤️',
    question: '신념/팬덤은?',
    description: '커뮤니티와 문화 형성',
    projects: ['메타시비아'],
    color: '#43e97b',
  },
  {
    stage: '신념/의미',
    icon: '✨',
    description: '가치와 영향력',
    color: '#fa709a',
  },
]

type Language = 'ko' | 'en'

const languageNames: Record<Language, string> = {
  ko: '한국어',
  en: 'English',
}

const translations: Record<Language, Record<string, string>> = {
  ko: {
    subtitle: '올해 우리가 추진하는 모든 프로젝트는 베트남 사파 지역 아동·청소년·여성의 치유와 상담 교육 프로그램을 위해 진행됩니다.',
    missionBody:
      '우리는 사파에 커뮤니티 센터를 만들고, 자원봉사자를 모집하며, 부모 교육을 제공해 문화적으로 소외된 이들에게 희망을 전할 계획입니다.',
    missionDonate: '당신의 소중한 1달러가 많은 이들에게 희망이 됩니다. 많이 후원해주세요 💝',
    donateCta: '☕ Support Our Mission',
    tabDashboard: '대시보드',
    tabStatus: '프로젝트 현황',
    tabLifecycle: '라이프사이클',
    filterAll: '전체',
    lifecycleTitle: 'Lifecycle Flow',
    lifecycleSubtitle: '개인의 탄생부터 팀, 생산, 투자, 팬덤, 신념까지 확장되는 프로젝트 생애 주기',
    layerTitle: '레이어 구조',
    layerMicro: 'Micro',
    layerMicroDesc: '개인 성장과 정체성 발견',
    layerMeso: 'Meso',
    layerMesoDesc: '팀/조직과 생산 시스템 구축',
    layerMacro: 'Macro',
    layerMacroDesc: '투자와 팬덤 기반의 확장',
    dashboardTitle: 'Project Performance Dashboard',
    dashboardSubtitle: 'Investment Revenue, Service Revenue & User Metrics',
    dashboardInvestmentRevenue: 'Investment Revenue',
    dashboardServiceRevenue: 'Service Revenue',
    dashboardUsers: 'Active Users',
    dashboardTotalRevenue: 'Total Revenue',
    dashboardProgress: 'Progress',
    dashboardTotalInvestment: 'Total Investment Revenue',
    dashboardTotalService: 'Total Service Revenue',
    dashboardTotalUsers: 'Total Active Users',
  },
  en: {
    subtitle: 'All projects we are working on this year are for healing and counseling education programs for children, youth, and women in Sapa, Vietnam.',
    missionBody:
      'We plan to create a community center there, recruit volunteers, and provide parent education to give hope to those who are culturally marginalized.',
    missionDonate: 'Your precious dollar will give hope to many marginalized people to live happily. I hope you donate a lot 💝',
    donateCta: '☕ Support Our Mission',
    tabDashboard: 'Dashboard',
    tabStatus: 'Status',
    tabLifecycle: 'Lifecycle',
    filterAll: 'All',
    lifecycleTitle: 'Lifecycle Flow',
    lifecycleSubtitle: 'From birth to team, production, investment, fandom, and belief—project life cycle',
    layerTitle: 'Layer Structure',
    layerMicro: 'Micro',
    layerMicroDesc: 'Personal growth and identity discovery',
    layerMeso: 'Meso',
    layerMesoDesc: 'Team building and production system',
    layerMacro: 'Macro',
    layerMacroDesc: 'Scaling with investment and fandom',
    dashboardTitle: 'Project Performance Dashboard',
    dashboardSubtitle: 'Investment Revenue, Service Revenue & User Metrics',
    dashboardInvestmentRevenue: 'Investment Revenue',
    dashboardServiceRevenue: 'Service Revenue',
    dashboardUsers: 'Active Users',
    dashboardTotalRevenue: 'Total Revenue',
    dashboardProgress: 'Progress',
    dashboardTotalInvestment: 'Total Investment Revenue',
    dashboardTotalService: 'Total Service Revenue',
    dashboardTotalUsers: 'Total Active Users',
  },
}

type TabType = 'dashboard' | 'status' | 'lifecycle'

const phaseBadgeColor = (phase: string) => {
  switch (phase) {
    case '운영 중':
      return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
    case '개발 중':
      return 'bg-sky-500/20 text-sky-300 border-sky-500/40'
    case '아이디어 구상':
      return 'bg-purple-500/20 text-purple-300 border-purple-500/40'
    case '베타 테스트':
      return 'bg-amber-500/20 text-amber-300 border-amber-500/40'
    case '런칭':
      return 'bg-rose-500/20 text-rose-300 border-rose-500/40'
    default:
      return 'bg-slate-500/20 text-slate-300 border-slate-500/40'
  }
}

export default function ProjectGallery() {
  const [language, setLanguage] = useState<Language>('ko')
  const [activeTab, setActiveTab] = useState<TabType>('dashboard')
  const [activeCategory, setActiveCategory] = useState('all')

  const t = (key: string) => translations[language][key] ?? translations.en[key] ?? key

  const categories = useMemo(() => ['all', ...projects.map((p) => p.category)], [])

  const filteredProjects = useMemo(() => {
    if (activeCategory === 'all') return projects
    return projects.filter((cat) => cat.category === activeCategory)
  }, [activeCategory])

  const totalInvestment = useMemo(
    () =>
      projects
        .flatMap((cat) => cat.items)
        .reduce((sum, p) => sum + p.investmentRevenue, 0),
    []
  )

  const totalService = useMemo(
    () =>
      projects
        .flatMap((cat) => cat.items)
        .reduce((sum, p) => sum + p.serviceRevenue, 0),
    []
  )

  const totalUsers = useMemo(
    () => projects.flatMap((cat) => cat.items).reduce((sum, p) => sum + p.users, 0),
    []
  )

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="rounded-2xl border border-slate-700 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">HAHAO PROJECT</h1>
            <p className="mt-2 text-slate-300">{t('subtitle')}</p>
            <p className="mt-2 text-sm text-slate-400">Civilization-as-a-Service</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 rounded-xl bg-slate-800/60 px-4 py-3 text-sm text-slate-300">
              <Globe className="h-5 w-5 text-sky-400" />
              <span>Global Project Portfolio</span>
            </div>
            <select
              value={language}
              onChange={(event) => setLanguage(event.target.value as Language)}
              className="rounded-lg bg-slate-800/80 px-3 py-2 text-sm text-slate-200"
            >
              {Object.entries(languageNames).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Mission Statement */}
      <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 text-rose-300">
            <Heart className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Mission Statement</h2>
          </div>
          <p className="text-sm leading-relaxed text-slate-300">{t('missionBody')}</p>
          <p className="text-sm text-rose-200">{t('missionDonate')}</p>
          <a
            href="https://buymeacoffee.com/goodprogram"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-400"
          >
            {t('donateCta')}
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-3">
        {(
          [
            { key: 'dashboard', label: `📈 ${t('tabDashboard')}` },
            { key: 'status', label: `📊 ${t('tabStatus')}` },
            { key: 'lifecycle', label: `🔄 ${t('tabLifecycle')}` },
          ] as { key: TabType; label: string }[]
        ).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeTab === tab.key
                ? 'bg-sky-500 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Status View */}
      {activeTab === 'status' && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  activeCategory === cat
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {cat === 'all' ? t('filterAll') : cat}
              </button>
            ))}
          </div>

          {filteredProjects.map((category) => (
            <div key={category.category} className="space-y-4">
              <h2 className="text-xl font-bold text-white">{category.category}</h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {category.items.map((project) => (
                  <a
                    key={project.name}
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group rounded-2xl border border-slate-700 bg-slate-800/60 p-5 transition hover:border-sky-500/60"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-700 text-sm font-semibold text-white">
                          {project.logo ? (
                            <img src={project.logo} alt={project.name} className="h-10 w-10 rounded-lg object-cover" />
                          ) : (
                            project.name.slice(0, 2)
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{project.name}</h3>
                          <p className="text-xs text-slate-400">{project.lifecycle}</p>
                        </div>
                      </div>
                      <span className={`rounded-full border px-2 py-1 text-xs ${phaseBadgeColor(project.phase)}`}>
                        {project.phase}
                      </span>
                    </div>

                    <p className="mt-3 text-sm text-slate-300">{project.description}</p>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>진행률</span>
                        <span>{project.progress}%</span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-700">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-sky-500 to-emerald-400"
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lifecycle View */}
      {activeTab === 'lifecycle' && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-700 bg-slate-800/60 p-6">
            <h2 className="text-xl font-bold text-white">⭕ {t('lifecycleTitle')}</h2>
            <p className="mt-2 text-sm text-slate-300">{t('lifecycleSubtitle')}</p>
          </div>

          <div className="space-y-6">
            {lifecycleFlow.map((stage, idx) => (
              <div key={stage.stage} className="space-y-3">
                <div
                  className="rounded-2xl border border-slate-700 bg-slate-800/60 p-5"
                  style={{ borderLeftColor: stage.color || '#64748b', borderLeftWidth: 4 }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{stage.icon}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{stage.stage}</h3>
                      {stage.question && <p className="text-xs text-slate-400">{stage.question}</p>}
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-slate-300">{stage.description}</p>
                </div>

                {stage.projects && stage.projects.length > 0 && (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {stage.projects.map((projectName) => {
                      const project = projects.flatMap((cat) => cat.items).find((p) => p.name === projectName)
                      if (!project) return null
                      return (
                        <a
                          key={project.name}
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-xl border border-slate-700 bg-slate-800/60 p-4 hover:border-sky-500/60"
                        >
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-white">{project.name}</h4>
                            <span className="text-xs text-slate-400">{project.progress}%</span>
                          </div>
                          <p className="mt-2 text-xs text-slate-300">{project.description}</p>
                          <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                            <span className={`rounded-full border px-2 py-0.5 ${phaseBadgeColor(project.phase)}`}>
                              {project.phase}
                            </span>
                            <span>{project.url.replace('https://', '')}</span>
                          </div>
                        </a>
                      )
                    })}
                  </div>
                )}

                {idx < lifecycleFlow.length - 1 && (
                  <div className="text-center text-slate-500">↓</div>
                )}
              </div>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {[
              {
                icon: <Sparkles className="h-5 w-5" />,
                title: t('layerMicro'),
                desc: t('layerMicroDesc'),
              },
              {
                icon: <Layers className="h-5 w-5" />,
                title: t('layerMeso'),
                desc: t('layerMesoDesc'),
              },
              {
                icon: <LineChart className="h-5 w-5" />,
                title: t('layerMacro'),
                desc: t('layerMacroDesc'),
              },
            ].map((layer) => (
              <div key={layer.title} className="rounded-2xl border border-slate-700 bg-slate-800/60 p-5">
                <div className="flex items-center gap-2 text-sky-300">
                  {layer.icon}
                  <h4 className="text-sm font-semibold text-white">{layer.title}</h4>
                </div>
                <p className="mt-2 text-xs text-slate-300">{layer.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dashboard View */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-700 bg-slate-800/60 p-6">
            <h2 className="text-xl font-bold text-white">📈 {t('dashboardTitle')}</h2>
            <p className="mt-2 text-sm text-slate-300">{t('dashboardSubtitle')}</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {projects.flatMap((cat) => cat.items).map((project) => (
              <div key={project.name} className="rounded-2xl border border-slate-700 bg-slate-800/60 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-700 text-sm font-semibold text-white">
                      {project.logo ? (
                        <img src={project.logo} alt={project.name} className="h-10 w-10 rounded-lg object-cover" />
                      ) : (
                        project.name.slice(0, 2)
                      )}
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white">{project.name}</h3>
                      <p className="text-xs text-slate-400">{project.phase}</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400">{project.progress}%</span>
                </div>

                <div className="mt-4 space-y-3">
                  <MetricBar
                    label={t('dashboardInvestmentRevenue')}
                    value={`$${(project.investmentRevenue / 1000000).toFixed(2)}M`}
                    variant="investment"
                  />
                  <MetricBar
                    label={t('dashboardServiceRevenue')}
                    value={`$${(project.serviceRevenue / 1000).toFixed(0)}K`}
                    variant="service"
                  />
                  <MetricBar
                    label={t('dashboardUsers')}
                    value={project.users.toLocaleString()}
                    variant="users"
                  />
                </div>

                <div className="mt-4 rounded-xl bg-slate-900/60 p-3 text-xs text-slate-300">
                  <div className="flex items-center justify-between">
                    <span>{t('dashboardTotalRevenue')}</span>
                    <span className="text-white">
                      ${((project.investmentRevenue + project.serviceRevenue) / 1000000).toFixed(2)}M
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <SummaryCard
              title={t('dashboardTotalInvestment')}
              value={`$${(totalInvestment / 1000000).toFixed(2)}M`}
            />
            <SummaryCard
              title={t('dashboardTotalService')}
              value={`$${(totalService / 1000000).toFixed(2)}M`}
            />
            <SummaryCard
              title={t('dashboardTotalUsers')}
              value={totalUsers.toLocaleString()}
            />
          </div>
        </div>
      )}

      <div className="text-center text-xs text-slate-500">
        © 2026 HAHAO PROJECT - All Rights Reserved
      </div>
    </div>
  )
}

function MetricBar({
  label,
  value,
  variant,
}: {
  label: string
  value: string
  variant: 'investment' | 'service' | 'users'
}) {
  const colorMap = {
    investment: 'from-emerald-500 to-emerald-300',
    service: 'from-sky-500 to-sky-300',
    users: 'from-purple-500 to-purple-300',
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span>{label}</span>
        <span className="text-slate-200">{value}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-700">
        <div className={`h-full rounded-full bg-gradient-to-r ${colorMap[variant]} w-4/5`} />
      </div>
    </div>
  )
}

function SummaryCard({ title, value }: { title: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-800/60 p-5">
      <p className="text-xs text-slate-400">{title}</p>
      <p className="mt-2 text-2xl font-bold text-white">{value}</p>
    </div>
  )
}
